/***
 *
 *   PANNEL
 *   Display an overlay pannel anywhere in your application by calling
 *   context.pannel.show() with an object containing the following props.
 *
 *   This is the slide-over the reference builds with framer-motion in
 *   ticket-detail-panel.tsx, and the surface it uses for `Sheet` elsewhere.
 *   Focus trap and Escape come from useOverlay; Escape is skipped while focus
 *   is in a field, matching the reference.
 *
 *   PROPS
 *   component: React component to display in the pannel
 *   styles: inline styles applied to the panel surface
 *   className: width/appearance overrides for the surface. Resolved through
 *     `cn`, so a caller's `max-w-*` / `w-*` beats the default cap — the ticket
 *     details drawer uses this to run much wider than the default sheet.
 *
 **********/

import { useContext, useEffect, useState } from 'react';
import { ViewContext, Icon, cn } from 'components/lib';
import { useOverlay } from 'components/hooks/useOverlay';
import Style from './pannel.tailwind.js';

export function Pannel(props) {
    const context = useContext(ViewContext);
    const close = () => context.pannel.hide(true);
    const ref = useOverlay({ onClose: close });

    // Mount off-canvas, then translate in on the next frame so the transition
    // actually runs. CSSTransition did this before via appear classes.
    const [entered, setEntered] = useState(false);
    useEffect(() => {
        const id = requestAnimationFrame(() => setEntered(true));
        return () => cancelAnimationFrame(id);
    }, []);

    return (
        <>
            <div className={Style.overlay} onClick={close} aria-hidden />

            <div
                ref={ref}
                role="dialog"
                aria-modal="true"
                tabIndex={-1}
                className={cn(Style.content, entered ? Style.open : Style.closed, props.className)}
                style={props.styles}>
                {!props.hideClose && (
                    <button type="button" className={Style.close} onClick={close} aria-label="Close">
                        <Icon image="x" size={18} color="currentColor" />
                    </button>
                )}

                {props.component}
            </div>
        </>
    );
}
